import * as React from "react";
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Table as ReactTableInstance,
  TableOptions,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "@/components/ui/customs/data-table/data-table-toolbar";
import clsx from "clsx";
import { SortStateIcons } from "@/components/shared/sort";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface DataTableProps<TData, TValue> extends Partial<TableOptions<TData>> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pagination?: PaginationState;
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
  sorting?: SortingState;
  setSorting?: React.Dispatch<React.SetStateAction<SortingState>>;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  initialColumnVisibility?: VisibilityState;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  rowSelection?: RowSelectionState;
  minTableWidth?: number;
  useFlexLayout?: boolean;
  hideHeader?: boolean;
  headerClassName?: string;
  rowClassname?: (row: TData) => string;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  onRowClick?: (row: TData) => void;
  wrapperClassName?: string;
  headCellClassName?: string;
  cellClassName?: string;
  renderDividerRow?: (row: TData) => React.ReactElement | null;
  isLoading?: boolean;
  skeletonRowClassName?: string;
}

const SKELETON_ROW_COUNT = 10;
const SKELETON_ROUNDED_THRESHOLD = 40;

function getColumnSizeStyle(column: Column<any, any>, useFlexLayout: boolean) {
  return {
    minWidth: column.getSize(),
    ...(useFlexLayout && {
      flex: (column.columnDef.meta as any)?.flex || 1,
      minWidth: column.columnDef.minSize || 40,
    }),
  };
}

function DataTableHead<TData>({
  table,
  headerClassName,
  headCellClassName,
  hideHeader,
  useFlexLayout,
}: {
  table: ReactTableInstance<TData>;
  headerClassName?: string;
  headCellClassName?: string;
  hideHeader: boolean;
  useFlexLayout: boolean;
}) {
  const { t } = useTranslation("shared");
  return (
    <TableHeader className={clsx("bg-gray-50", headerClassName, hideHeader && "hidden")}>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHead
              key={header.id}
              colSpan={header.colSpan}
              className={clsx(headCellClassName)}
              style={getColumnSizeStyle(header.column, useFlexLayout)}
            >
              <div
                className={clsx(
                  "group flex items-center gap-1",
                  header.column.getCanSort() ? "cursor-pointer select-none" : "",
                )}
                onClick={header.column.getToggleSortingHandler()}
                title={
                  header.column.getCanSort()
                    ? header.column.getNextSortingOrder() === "asc"
                      ? t("dataTable.sortAscending")
                      : header.column.getNextSortingOrder() === "desc"
                        ? t("dataTable.sortDescending")
                        : t("dataTable.clearSort")
                    : undefined
                }
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getCanSort() &&
                  SortStateIcons(header.column.getIsSorted() as "asc" | "desc" | null)}
              </div>
            </TableHead>
          ))}
        </TableRow>
      ))}
    </TableHeader>
  );
}

function SkeletonCell({
  column,
  useFlexLayout,
  cellClassName,
}: {
  column: Column<any, any>;
  useFlexLayout: boolean;
  cellClassName?: string;
}) {
  const size = column.getSize();
  const skeletonWidth = Math.max(12, Math.min(size * 0.75, size - 8));
  const isSmall = skeletonWidth < SKELETON_ROUNDED_THRESHOLD;

  return (
    <TableCell className={clsx(cellClassName)} style={getColumnSizeStyle(column, useFlexLayout)}>
      <Skeleton
        className={clsx("bg-gray-200", isSmall ? "rounded-full" : "h-4 rounded-md")}
        style={{ width: skeletonWidth, height: isSmall ? skeletonWidth : undefined }}
      />
    </TableCell>
  );
}

function SkeletonRows<TData>({
  table,
  cellClassName,
  skeletonRowClassName,
  useFlexLayout,
}: {
  table: ReactTableInstance<TData>;
  cellClassName?: string;
  skeletonRowClassName?: string;
  useFlexLayout: boolean;
}) {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <TableRow key={`skeleton-row-${rowIndex}`} className={clsx(skeletonRowClassName)}>
          {table.getVisibleLeafColumns().map((column) => (
            <SkeletonCell
              key={column.id}
              column={column}
              useFlexLayout={useFlexLayout}
              cellClassName={cellClassName}
            />
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyRow({ colSpan }: { colSpan: number }) {
  const { t } = useTranslation("shared");
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-24 text-center">
        {t("dataTable.empty")}
      </TableCell>
    </TableRow>
  );
}

function DataTableRow<TData>({
  row,
  columnsLength,
  cellClassName,
  rowClassname,
  useFlexLayout,
  onRowClick,
  renderSubComponent,
  renderDividerRow,
}: {
  row: Row<TData>;
  columnsLength: number;
  cellClassName?: string;
  rowClassname?: (row: TData) => string;
  useFlexLayout: boolean;
  onRowClick?: (row: TData) => void;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  renderDividerRow?: (row: TData) => React.ReactElement | null;
}) {
  const dividerContent = renderDividerRow?.(row.original);
  if (dividerContent) {
    return (
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={columnsLength} className="p-0">
          {dividerContent}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        data-state={row.getIsSelected() ? "selected" : undefined}
        onClick={() => {
          if (row.getCanExpand()) {
            row.toggleExpanded();
          } else if (onRowClick) {
            onRowClick(row.original);
          }
        }}
        className={clsx(
          rowClassname?.(row.original),
          onRowClick && !row.getCanExpand() && "cursor-pointer",
        )}
      >
        {row.getVisibleCells().map((cell) => {
          const metaCellClassName = (cell.column.columnDef.meta as any)?.cellClassName;
          const resolvedMetaClassName =
            typeof metaCellClassName === "function"
              ? metaCellClassName(cell.row.original)
              : metaCellClassName;
          return (
            <TableCell
              key={cell.id}
              className={clsx(cellClassName, !row.getIsSelected() && resolvedMetaClassName)}
              style={getColumnSizeStyle(cell.column, useFlexLayout)}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
      {row.getIsExpanded() && renderSubComponent && (
        <TableRow className="bg-gray-50">
          <TableCell colSpan={row.getVisibleCells().length}>
            {renderSubComponent({ row })}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pagination,
  setPagination,
  sorting,
  setSorting,
  columnFilters,
  setColumnFilters,
  initialColumnVisibility,
  enableRowSelection = false,
  setRowSelection,
  rowSelection,
  minTableWidth,
  useFlexLayout = false,
  hideHeader = false,
  headerClassName,
  rowClassname,
  renderSubComponent,
  getRowCanExpand = () => false,
  onRowClick,
  wrapperClassName,
  headCellClassName,
  cellClassName,
  renderDividerRow,
  isLoading = false,
  skeletonRowClassName,
  ...tableOptions
}: DataTableProps<TData, TValue>) {
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialColumnVisibility || {},
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  useEffect(() => {
    setExpanded({});
  }, [data, setExpanded]);

  const table = useReactTable({
    ...tableOptions,
    data,
    columns,
    getRowCanExpand,
    state: {
      sorting,
      pagination,
      columnVisibility,
      expanded,
      rowSelection: rowSelection ?? {},
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),

    onPaginationChange: setPagination,
    manualPagination: setPagination && pagination ? true : false,
    autoResetPageIndex: false,

    manualSorting: true,
    enableSorting: true,
    onSortingChange: setSorting,

    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,

    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: (updater) =>
      setExpanded((old) => {
        const next = typeof updater === "function" ? updater(old) : updater;
        if (typeof next !== "object") return next;
        const previous = typeof old === "object" ? old : {};
        const openedRowId = Object.keys(next).find((rowId) => next[rowId] && !previous[rowId]);
        return openedRowId ? { [openedRowId]: true } : {};
      }),
    getSubRows: (row: any) => row.billingEntities,

    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const rows = table.getRowModel().rows;

  return (
    <div className={clsx("flex flex-1 flex-col gap-4 overflow-auto", wrapperClassName)}>
      <DataTableToolbar table={table} hideViewOptions={!!columnFilters} />
      <div className="overflow-auto rounded-xl border bg-white">
        <Table
          style={{
            width: "100%",
            minWidth: Math.max(table.getCenterTotalSize(), minTableWidth || 0),
          }}
          className={`w-full ${useFlexLayout ? "table-fixed" : ""}`}
        >
          <DataTableHead
            table={table}
            headerClassName={headerClassName}
            headCellClassName={headCellClassName}
            hideHeader={hideHeader}
            useFlexLayout={useFlexLayout}
          />
          <TableBody>
            {isLoading ? (
              <SkeletonRows
                table={table}
                cellClassName={cellClassName}
                skeletonRowClassName={skeletonRowClassName}
                useFlexLayout={useFlexLayout}
              />
            ) : rows.length ? (
              rows.map((row) => (
                <DataTableRow
                  key={row.id}
                  row={row}
                  columnsLength={columns.length}
                  cellClassName={cellClassName}
                  rowClassname={rowClassname}
                  useFlexLayout={useFlexLayout}
                  onRowClick={onRowClick}
                  renderSubComponent={renderSubComponent}
                  renderDividerRow={renderDividerRow}
                />
              ))
            ) : (
              <EmptyRow colSpan={columns.length} />
            )}
          </TableBody>
        </Table>
      </div>
      {!!pagination && <DataTablePagination table={table} />}
    </div>
  );
}
